import { SUPPORTED_LANGUAGES, type CourseStatus, type SupportedLanguages } from "@repo/shared";
import { useQuery, useSuspenseQuery } from "@tanstack/react-query";

import { ApiClient } from "../api-client";

import type { GetAllCoursesResponse } from "../generated-api";

export type { CourseStatus };

const ALL_COURSES_SORTABLE_FIELDS = ["title", "author", "category", "creationDate"] as const;

type AllCoursesSortField = (typeof ALL_COURSES_SORTABLE_FIELDS)[number];
export type AllCoursesSort = AllCoursesSortField | `-${AllCoursesSortField}`;

export type CourseParams = {
  /** Filter by course title only */
  title?: string;
  /** Filter by course description only */
  description?: string;
  /** Search across both title AND description fields simultaneously */
  searchQuery?: string;
  category?: string;
  state?: CourseStatus;
  sort?: AllCoursesSort;
  authorId?: string;
  archived?: boolean;
  language?: SupportedLanguages;
  page?: number;
  perPage?: number;
};

type QueryOptions = {
  enabled?: boolean;
};

export const ALL_COURSES_QUERY_KEY = ["courses"];

export const ALL_COURSES_PAGE_SIZE = 10;

const getAllCoursesRequestParams = (
  searchParams: CourseParams | undefined,
  page: number,
  perPage: number,
) => ({
  ...(searchParams?.title && { title: searchParams.title }),
  ...(searchParams?.description && { description: searchParams.description }),
  ...(searchParams?.searchQuery && { searchQuery: searchParams.searchQuery }),
  ...(searchParams?.category && { category: searchParams.category }),
  ...(searchParams?.authorId && { authorId: searchParams.authorId }),
  ...(searchParams?.state && { status: searchParams.state }),
  ...(searchParams?.sort && { sort: searchParams.sort }),
  ...(searchParams?.archived && { archived: searchParams.archived }),
  language: searchParams?.language ?? SUPPORTED_LANGUAGES.EN,
  page,
  perPage,
});

export const allCoursesQueryOptions = (
  searchParams?: CourseParams,
  options: QueryOptions = { enabled: true },
) => ({
  queryKey: [...ALL_COURSES_QUERY_KEY, searchParams],
  queryFn: async () => {
    const response = await ApiClient.api.courseControllerGetAllCourses(
      getAllCoursesRequestParams(searchParams, 1, 100),
    );
    return response.data;
  },
  select: (data: GetAllCoursesResponse) => data.data,
  ...options,
});

export const paginatedCoursesQueryOptions = (searchParams?: CourseParams) => ({
  placeholderData: (previousData: GetAllCoursesResponse | undefined) => previousData,
  queryKey: [...ALL_COURSES_QUERY_KEY, "paginated", searchParams],
  queryFn: async () => {
    const response = await ApiClient.api.courseControllerGetAllCourses(
      getAllCoursesRequestParams(
        searchParams,
        searchParams?.page ?? 1,
        searchParams?.perPage ?? ALL_COURSES_PAGE_SIZE,
      ),
    );
    return response.data;
  },
});

export function useCourses(searchParams?: CourseParams, options?: QueryOptions) {
  return useQuery(allCoursesQueryOptions(searchParams, options));
}

export function useCoursesSuspense(searchParams?: CourseParams, options?: QueryOptions) {
  return useSuspenseQuery(allCoursesQueryOptions(searchParams, options));
}

export function usePaginatedCoursesSuspense(searchParams?: CourseParams) {
  return useSuspenseQuery(paginatedCoursesQueryOptions(searchParams));
}
